import type {FormInstance} from 'antd';
import {Form, Input, Typography} from 'antd';
import React from 'react';
import {useTranslation} from '../../../hooks/useTranslation';
import {hexToRgba} from '../../../utils/ColorHelper';
import {isFormulaValid} from '../../../utils/formulaEngine';

const {Text} = Typography;
const {TextArea} = Input;

export interface AssessmentOptionFormValues {
  label: string;
  scaleBand: string | number;
  formula?: string;
}

interface AssessmentOptionFormProps {
  form: FormInstance<AssessmentOptionFormValues>;
  scaleBandOptions: any[];
  showFormula?: boolean;
  targetKind?: 'group' | 'variable';
  targetName?: string;
}

const AssessmentOptionForm: React.FC<AssessmentOptionFormProps> = ({
                                                                     form,
                                                                     scaleBandOptions,
                                                                     showFormula,
                                                                     targetKind,
                                                                     targetName,
                                                                   }) => {
  const {t} = useTranslation();
  const requiredMessage = t('messages.notFound');

  return (
    <Form form={form} layout="vertical" preserve={false}>
      {targetKind && targetName && (
        <div className="gt-ac-drawer-target">
          <Text strong>{targetName}</Text>
          <Text type="secondary">
            ·{' '}
            {targetKind === 'group'
              ? t('settings.content.impactAssessment.optionPanel.meta.kindGroup')
              : t('settings.content.impactAssessment.optionPanel.meta.kindVariable')}
          </Text>
        </div>
      )}

      <Form.Item
        name="label"
        label={t('settings.content.impactAssessment.table.columns.name')}
        rules={[{required: true, message: requiredMessage}]}
      >
        <TextArea
          autoSize={{maxRows: 3}}
          maxLength={200}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="scaleBand"
        label={t('settings.content.impactAssessment.optionDrawer.bandPickerLabel')}
        rules={[{required: true, message: requiredMessage}]}
      >
        <BandCardGrid options={scaleBandOptions}/>
      </Form.Item>

      {showFormula && (
        <Form.Item
          name="formula"
          label={t('settings.content.impactAssessment.formulaTable.columns.formula')}
          rules={[
            {required: true, message: requiredMessage},
            {
              validator: (_, value: string) =>
                isFormulaValid(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(t('settings.content.impactAssessment.scaleDrawer.formulaInvalid'))
                    ),
            },
          ]}
        >
          <TextArea
            autoSize={{maxRows: 4}}
            maxLength={300}
            showCount
          />
        </Form.Item>
      )}
    </Form>
  );
};

interface BandCardGridProps {
  value?: string | number;
  onChange?: (value: string | number) => void;
  options: any[];
}

const BandCardGrid: React.FC<BandCardGridProps> = ({value, onChange, options}) => {
  return (
    <div className="gt-ac-band-grid">
      {options.map((band) => {
        const selected = String(band.id) === String(value);
        const accent = band.color || '#9fa9bd';
        return (
          <button
            key={band.id}
            type="button"
            className={`gt-ac-band-card${selected ? ' gt-ac-band-card-selected' : ''}`}
            style={selected ? {
              borderColor: accent,
              backgroundColor: hexToRgba(accent, 0.08),
            } : undefined}
            onClick={() => onChange?.(band.id)}
            aria-pressed={selected}
          >
            <span className="gt-ac-band-card-row">
              <span className="gt-ac-band-card-swatch" style={{backgroundColor: accent}}/>
              <span className="gt-ac-band-card-label">{band.label}</span>
              {band.displayOrder != null && (
                <span className="gt-ac-band-card-order">{band.displayOrder}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default AssessmentOptionForm;
